import { inject } from 'vue';
import { i18n } from '../locale';

interface MessageBoxOptions {
  type?: string;
  message: string;
  title?: string;
  iconClass?: string;
  readonly onClose?: (type: string) => void;
  showClose?: boolean;
  showConfirmBtn?: boolean;
  showCancelBtn?: boolean;
  confirmBtnText?: string;
  cancelBtnText?: string;
  closeOnClickModal?: boolean;
  useHTML?: boolean;
  focusable?: boolean;
}
export type MessageBoxType = (value?: string | MessageBoxOptions) => void;

export const useMessageBox = () => {
  const messageBox = inject<MessageBoxType>('messageBox');
  if (!messageBox) {
    throw new Error('useMessageBox must be used within a MessageBoxProvider');
  }

  const confirmMsg = ({
    msgStr,
    okCallback,
    cancelCallback,
    title,
    useHTML,
    confirmBtnText = i18n.global.t('WORD.OK'),
    showClose = false,
    showConfirmBtn = true,
    showCancelBtn = true,
  }: {
    msgStr: string;
    okCallback?: () => void;
    cancelCallback?: () => void;
    title?: string;
    useHTML?: boolean;
    showClose?: boolean;
    confirmBtnText?: string;
    showConfirmBtn?: boolean;
    showCancelBtn?: boolean;
  }) => {
    messageBox({
      message: msgStr,
      title,
      useHTML,
      closeOnClickModal: false,
      showClose,
      showConfirmBtn,
      showCancelBtn,
      focusable: true,
      onClose: (type) => {
        if (type === 'ok') {
          okCallback?.();
        } else if (typeof cancelCallback === 'function') {
          cancelCallback?.();
        }
      },
      confirmBtnText,
      cancelBtnText: i18n.global.t('WORD.CANCEL'),
    });
  };

  const promiseConfirmMsg = (...args: Parameters<typeof confirmMsg>) => {
    return new Promise<boolean>((resolve) => {
      confirmMsg({
        ...args[0],
        okCallback: () => {
          args[0].okCallback?.();
          resolve(true);
        },
        cancelCallback: () => {
          args[0].cancelCallback?.();
          resolve(false);
        },
      });
    });
  };

  return { confirmMsg, promiseConfirmMsg };
};
